import { t } from '../i18n/translations';
import type { Language } from '../i18n/translations';
import type { LearningLanguage } from '../types';

interface GenerateSettingsProps {
  sentenceCount: string;
  wordsPerSentence: string;
  tenses: { present: boolean; past: boolean; future: boolean };
  grammaticalCase?: string;
  learningLanguage?: LearningLanguage;
  topicEnabled?: boolean;
  topic?: string;
  rememberTopic?: boolean;
  topicInputMode?: boolean;
  savedTopics?: string[];
  onSentenceCountChange: (v: string) => void;
  onWordsPerSentenceChange: (v: string) => void;
  onTenseChange: (tense: 'present' | 'past' | 'future', checked: boolean) => void;
  onGrammaticalCaseChange?: (value: string) => void;
  onTopicChange?: (value: string) => void;
  onRememberTopicChange?: (checked: boolean) => void;
  onCreateNewTopic?: () => void;
  onUseSavedTopics?: () => void;
  onDeleteTopic?: () => void;
  language?: Language;
}

const CREATE_NEW_TOPIC_VALUE = '__create_new_topic__';

const CASE_OPTIONS: Record<LearningLanguage, { value: string; label: string }[]> = {
  pl: [
    { value: 'nominative', label: 'mianownik' },
    { value: 'genitive', label: 'dopełniacz' },
    { value: 'dative', label: 'celownik' },
    { value: 'accusative', label: 'biernik' },
    { value: 'instrumental', label: 'narzędnik' },
    { value: 'locative', label: 'miejscownik' },
    { value: 'vocative', label: 'wołacz' },
  ],
  lt: [
    { value: 'nominative', label: 'vardininkas' },
    { value: 'genitive', label: 'kilmininkas' },
    { value: 'dative', label: 'naudininkas' },
    { value: 'accusative', label: 'galininkas' },
    { value: 'instrumental', label: 'įnagininkas' },
    { value: 'locative', label: 'vietininkas' },
    { value: 'vocative', label: 'šauksmininkas' },
  ],
};

export default function GenerateSettings({
  sentenceCount,
  wordsPerSentence,
  tenses,
  grammaticalCase = '',
  learningLanguage = 'pl',
  topicEnabled = true,
  topic = '',
  rememberTopic = false,
  topicInputMode = false,
  savedTopics = [],
  onSentenceCountChange,
  onWordsPerSentenceChange,
  onTenseChange,
  onGrammaticalCaseChange,
  onTopicChange,
  onRememberTopicChange,
  onCreateNewTopic,
  onUseSavedTopics,
  onDeleteTopic,
  language = 'en',
}: GenerateSettingsProps) {
  const activeTenses: string[] = [];
  if (tenses.present) activeTenses.push(t(language, 'settings.present'));
  if (tenses.past) activeTenses.push(t(language, 'settings.past'));
  if (tenses.future) activeTenses.push(t(language, 'settings.future'));
  const tenseLabel = activeTenses.length > 0 ? activeTenses.join(', ') : t(language, 'settings.present');
  const showTopicInput = topicInputMode || savedTopics.length === 0;
  const canDeleteTopic = !showTopicInput && Boolean(topic.trim()) && Boolean(onDeleteTopic);
  const caseOptions = CASE_OPTIONS[learningLanguage] || CASE_OPTIONS.pl;

  return (
    <div className="generate-settings">
      <div className="settings-row">
        <label className="setting-label">
          <span>{t(language, 'settings.sentenceCount')}</span>
          <select
            id="sentence-count"
            value={sentenceCount}
            onChange={(e) => onSentenceCountChange(e.target.value)}
          >
            <option value="3">3</option>
            <option value="5">5</option>
            <option value="7">7</option>
            <option value="10">10</option>
          </select>
        </label>
        <label className="setting-label">
          <span>{t(language, 'settings.wordsPerSentence')}</span>
          <select
            id="words-per-sentence"
            value={wordsPerSentence}
            onChange={(e) => onWordsPerSentenceChange(e.target.value)}
          >
            <option value="3-5">3–5</option>
            <option value="4-8">4–8</option>
            <option value="4-10">4–10</option>
            <option value="6-12">6–12</option>
            <option value="8-15">8–15</option>
          </select>
        </label>
        <details className="tense-dropdown">
          <summary className="setting-label">
            <span>{t(language, 'settings.tense')}</span>
            <span className="tense-summary-value">{tenseLabel}</span>
          </summary>
          <div className="tense-dropdown-content">
            <label className="tense-label">
              <input
                type="checkbox"
                id="tense-present"
                checked={tenses.present}
                onChange={(e) => onTenseChange('present', e.target.checked)}
              />
              {` ${t(language, 'settings.present')}`}
            </label>
            <label className="tense-label">
              <input
                type="checkbox"
                id="tense-past"
                checked={tenses.past}
                onChange={(e) => onTenseChange('past', e.target.checked)}
              />
              {` ${t(language, 'settings.past')}`}
            </label>
            <label className="tense-label">
              <input
                type="checkbox"
                id="tense-future"
                checked={tenses.future}
                onChange={(e) => onTenseChange('future', e.target.checked)}
              />
              {` ${t(language, 'settings.future')}`}
            </label>
          </div>
        </details>
      </div>
      {topicEnabled && (
        <div className="settings-row settings-row-topic">
          <label className="setting-label setting-label-topic">
            <span>{t(language, 'settings.topic')}</span>
            <div className="topic-control-group">
              {showTopicInput ? (
                <input
                  id="topic-input"
                  className="topic-input"
                  type="text"
                  value={topic}
                  placeholder={t(language, 'topic.random')}
                  onChange={(e) => onTopicChange?.(e.target.value)}
                />
              ) : (
                <select
                  id="topic-select"
                  className="topic-select"
                  value={topic}
                  onChange={(e) => {
                    if (e.target.value === CREATE_NEW_TOPIC_VALUE) {
                      onCreateNewTopic?.();
                      return;
                    }
                    onTopicChange?.(e.target.value);
                  }}
                >
                  <option value="">{t(language, 'topic.random')}</option>
                  {savedTopics.map((savedTopic) => (
                    <option key={savedTopic} value={savedTopic}>{savedTopic}</option>
                  ))}
                  <option value={CREATE_NEW_TOPIC_VALUE}>{t(language, 'topic.createNew')}</option>
                </select>
              )}

              {showTopicInput && savedTopics.length > 0 && (
                <button
                  type="button"
                  id="topic-use-saved"
                  className="btn btn-sm btn-outline"
                  onClick={onUseSavedTopics}
                >
                  {t(language, 'topic.useSaved')}
                </button>
              )}

              {canDeleteTopic && (
                <button
                  type="button"
                  id="topic-delete"
                  className="btn btn-sm btn-outline btn-danger-outline"
                  onClick={onDeleteTopic}
                  title={t(language, 'topic.delete')}
                >
                  {t(language, 'btn.delete')}
                </button>
              )}
            </div>
          </label>

          <label className="topic-remember-label">
            <input
              id="topic-remember"
              type="checkbox"
              checked={rememberTopic}
              onChange={(e) => onRememberTopicChange?.(e.target.checked)}
            />
            <span>{t(language, 'topic.remember')}</span>
          </label>
        </div>
      )}
      <div className="settings-row settings-row-case">
        <label className="setting-label setting-label-case">
          <span>{t(language, 'settings.case')}</span>
          <select
            id="case-select"
            className="case-select"
            value={grammaticalCase}
            onChange={(e) => onGrammaticalCaseChange?.(e.target.value)}
          >
            <option value="">{t(language, 'case.random')}</option>
            {caseOptions.map((caseOption) => (
              <option key={caseOption.value} value={caseOption.value}>{caseOption.label}</option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}
